import { useTranslation } from 'react-i18next';

import { useConfig } from '@entities/config/api';
import { useGuestSession } from '@entities/guest';
import { useMe } from '@entities/user/api';
import { toSupportedLocale } from '@shared/i18n';
import { money } from '@shared/lib/money';
import { percentFromBps } from '@shared/lib/percent';

/**
 * Цена услуги и условия вознаграждения — **только из конфигурации** (FE-210,
 * FE-586, FE-587; DESIGN_SYSTEM §8).
 *
 * Хардкод цены в интерфейсе и локалях запрещён (CLAUDE.md §6): зашитая строка
 * разошлась бы с `SERVICE_PRICE_CENTS` молча. Нет конфигурации — нет и строки
 * цены, а не выдуманная сумма. Условия вознаграждения стоят на том же экране,
 * что цена: умолчание о втором платеже превращает честную модель в обман
 * (MVP_PRD §35.5).
 */
export function useServiceOffer() {
  const { t, i18n } = useTranslation();
  const { data: config } = useConfig();
  const locale = toSupportedLocale(i18n.resolvedLanguage);
  const payments = config?.payments;
  const fee = config?.success_fee;

  return {
    /** «19,90 €» по локали; `null` — конфигурации ещё нет. */
    price: payments ? money(payments.price_cents, payments.currency, locale) : null,
    /** Строка условий вознаграждения; `null` — выключено или конфигурации нет. */
    feeTerms:
      payments && fee?.enabled
        ? t('negotiation.mandate.feeTerms', {
            rate: percentFromBps(fee.rate_bps, locale),
            flat: money(fee.flat_cents, payments.currency, locale),
          })
        : null,
  };
}

/**
 * Кто перед нами: вошедший пользователь, гость или пока неизвестно.
 *
 * От этого зависит, куда ведёт переход к оплате: вошедшего — на экран оплаты,
 * гостя — в окно входа (регистрация вызывается ровно при попытке оплатить,
 * MVP_PRD §41.1). Пока ответа нет, кнопки нет вовсе: красная, ведущая не туда,
 * хуже никакой.
 */
export function useOwnerKind(): 'user' | 'guest' | 'unknown' {
  const me = useMe();
  const guest = useGuestSession(!me.isPending && me.data == null);
  if (me.data != null) return 'user';
  if (guest.data != null) return 'guest';
  return 'unknown';
}
